import * as PIXI from 'pixi.js';

export class TiledMesh extends PIXI.MeshSimple {
	/**
	 *
	 * @param {PIXI.Texture} texture
	 * @param {number[] | Float32Array} vertices
	 * @param {number[] | Float32Array} uvs
	 * @param {number[] | Int16Array} indices
	 */
	constructor(texture, vertices, uvs, indices) {
		super({texture, vertices: Float32Array.from(vertices), uvs: Float32Array.from(uvs), indices: Uint32Array.from(indices)});
		// TiledMesh uploads both buffers itself in updateMeshVerticeRotation().
		// Keeping MeshSimple's automatic onRender upload is redundant and can leave
		// a stale render-group callback touching the geometry during mass teardown.
		this.autoUpdate = false;
		this.onRender = null;

		/**
		 * @type {PIXI.Sprite}
		 */
		this.tex = texture;
		this.targetSprite = null;
		this.cachedSpriteRotation = 0;
		this.verticesClone = Float32Array.from(vertices);
		this.fixedTextureRotationOffset = Math.PI / 2;
		this.gradientMode = false;
	}

	/**
	 * A TiledMesh's texture is ALWAYS shared and owned elsewhere: either a cached,
	 * Assets-managed tile texture (PIXI.Texture.from('WoodHorizontal', ...)) or a
	 * gradient whose source lives in B2dEditor.levelGradientBaseTextures. Pixi v8's
	 * Container.destroy forwards its options into every child, so a level teardown
	 * that destroys a tiled body with {textureSource:true} would cascade in here and
	 * destroy that shared source -- nulling the texture for every other user and for
	 * the next level that re-fetches it by name. Force texture/source destruction off
	 * so the mesh only ever tears down its own geometry.
	 */
	destroy(options) {
		const opts = typeof options === 'boolean' ? { children: options } : { ...(options || {}) };
		opts.texture = false;
		opts.textureSource = false;
		super.destroy(opts);
	}

	updateMeshVerticeRotation (force = false) {

		const targetSprite = this.targetSprite;
		const tex = this.tex;

		if(this.cachedSpriteRotation !== targetSprite.rotation || force){

			const positionBuffer = this.geometry.getBuffer("aPosition");
			const uvBuffer = this.geometry.getBuffer("aUV");
			const vertices = positionBuffer.data;
			const angle = targetSprite.rotation + this.fixedTextureRotationOffset;
			const cosine = Math.cos(angle);
			const sine = Math.sin(angle);

			for (let i = 0; i < vertices.length; i += 2){
				const x = this.verticesClone[i];
				const y = this.verticesClone[i+1];
				vertices[i] = x * cosine - y * sine;
				vertices[i+1] = x * sine + y * cosine;
			}

			let minX = Number.POSITIVE_INFINITY;
			let maxX = -Number.POSITIVE_INFINITY;
			let minY = Number.POSITIVE_INFINITY;
			let maxY = -Number.POSITIVE_INFINITY;

			const uvs = uvBuffer.data;

			for (let i = 0; i < vertices.length; i+=2) {
				uvs[i] = vertices[i] * 2.0 / tex.width + 0.5;
				uvs[i + 1] = vertices[i + 1] * 2.0 / tex.width + 0.5;

				minX = Math.min(uvs[i + 0], minX);
				maxX = Math.max(uvs[i + 0], maxX);
				minY = Math.min(uvs[i + 1], minY);
				maxY = Math.max(uvs[i + 1], maxY);
			}

			if (this.gradientMode) {
				const uvWidth = maxX - minX || 1;
				const uvHeight = maxY - minY || 1;
				for (let i = 0; i < uvs.length; i += 2) {
					uvs[i + 1] = (uvs[i + 1] - minY) / uvHeight;
					uvs[i + 0] = (uvs[i + 0] - minX) / uvWidth;
				}
			}

			positionBuffer.update();
			uvBuffer.update();

			this.rotation = -targetSprite.rotation-this.fixedTextureRotationOffset;
			this.cachedSpriteRotation = targetSprite.rotation
		}
	}
}
